import React, { useEffect, useMemo, useState } from 'react';
import { Clock, RefreshCw, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import apiClient from '@/services/apiClient';

const fmt = (ts) => (ts ? new Date(ts).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

export const PendingFgList = ({ reloadKey }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get('/flexi/fg-assignment/pending');
      setRows(data.bundles || []);
    } catch { setRows([]); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [reloadKey]);

  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter((r) => [r.bundle_code, r.design_name, r.flow_type, r.transfer_id, r.received_from]
      .some((v) => String(v || '').toLowerCase().includes(t)));
  }, [rows, q]);

  return (
    <Card className="bg-white border-2 border-amber-200 shadow-md" data-testid="pending-fg-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Clock className="w-5 h-5 text-amber-500" /> Pending FG
            <Badge className="bg-amber-100 text-amber-800" data-testid="pending-fg-count">{rows.length}</Badge>
          </CardTitle>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search bundle / design…"
                className="pl-8 h-9 w-56" data-testid="pending-fg-search" />
            </div>
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="pending-fg-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        <p className="text-xs text-gray-500">Bundles received in Flexi that have no FG yet — they can't be JW-tagged or transferred out until they get one.</p>
      </CardHeader>
      <CardContent>
        <div className="max-h-80 overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Bundle</TableHead><TableHead>Flow</TableHead><TableHead>Design</TableHead>
                <TableHead>From</TableHead><TableHead>Document</TableHead><TableHead>Received</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!shown.length && (
                <TableRow><TableCell colSpan={6} className="text-center text-gray-400 py-6" data-testid="pending-fg-empty">
                  {loading ? 'Loading…' : 'No bundles waiting for an FG'}
                </TableCell></TableRow>
              )}
              {shown.map((r) => (
                <TableRow key={r.bundle_code} data-testid={`pending-fg-row-${r.bundle_code}`}>
                  <TableCell className="font-mono text-xs font-semibold">{r.bundle_code}</TableCell>
                  <TableCell>{r.flow_type ? <Badge variant="outline" className="text-[10px]">{r.flow_type}</Badge> : '—'}</TableCell>
                  <TableCell className="text-xs">{r.design_name || '—'}</TableCell>
                  <TableCell className="text-xs">{r.received_from || '—'}</TableCell>
                  <TableCell className="font-mono text-xs">{r.transfer_id || '—'}</TableCell>
                  <TableCell className="text-xs text-gray-500">{fmt(r.received_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};
